"use client";
import { useEffect, useState, useTransition } from "react";
import { uitaTot } from "@/lib/memo-client";
import Link from "next/link";
import { I, SEL } from "@/components/ui/icons";
import { loginAction, loginTotpAction } from "@/app/login/actions";
import { CaptchaField } from "@/components/auth/captcha-field";

const BTN = "inline-flex items-center justify-center gap-2 rounded-[var(--l-r-ctl)] bg-accent px-4 py-2.5 text-[14px] font-semibold text-white shadow-card transition enabled:hover:bg-accent-ink disabled:opacity-60";
const Spinner = () => <span className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />;

/** `resetat` = a venit din fluxul de recuperare a parolei; îi confirmăm că a mers.
 *  `next` = pagina cerută înainte de login (validată pe server), unde ajunge după ambii pași. */
export function LoginForm({ resetat, next }: { resetat?: boolean; next?: string }) {
  /* Ecranul de autentificare nu moștenește nimic din tab: numele, firmele și contorul celui
     dinainte se uită aici (vezi `memo-client.ts`), indiferent cum s-a ajuns aici. */
  useEffect(() => { uitaTot(); }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [capToken, setCapToken] = useState("");
  const [capCode, setCapCode] = useState("");
  const [capReload, setCapReload] = useState(0);
  const [pending, start] = useTransition();

  const submit = () => {
    if (!email.trim() || !password) { setError("Completează emailul și parola."); return; }
    if (!capCode) { setError("Scrie și codul din imagine."); return; }
    setError("");
    start(async () => {
      const r = await loginAction(email.trim(), password, { token: capToken, code: capCode }, next);
      if (r?.goTo) { window.location.assign(r.goTo); return; }
      if (r?.challenge) { setChallenge(r.challenge); setPassword(""); return; }
      /* Jetonul CAPTCHA e de unică folosință — orice încercare eșuată cere o imagine nouă. */
      if (r?.error) { setError(r.error); setCapReload((n) => n + 1); }
    });
  };

  const submitCode = () => {
    setError("");
    start(async () => {
      const r = await loginTotpAction(challenge ?? "", code, next);
      if (r?.goTo) { window.location.assign(r.goTo); return; }
      if (r?.error) { setError(r.error); setCode(""); }
    });
  };

  /* Pasul doi: ecran separat, nu un câmp în plus sub parolă. Parola a fost deja acceptată —
     amestecarea lor l-ar face pe om să creadă că trebuie s-o rescrie. */
  if (challenge) {
    return (
      <div className="rounded-[14px] border border-line bg-surface p-6 shadow-card">
        <p className="font-display text-[19px] font-medium text-ink">Verificare în doi pași</p>
        <p className="mt-1 text-[13px] leading-5 text-ink-3">Scrie codul de 6 cifre din aplicația ta de autentificare.</p>
        <form className="mt-5 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submitCode(); }}>
          <div>
            <label className="mb-1 block text-[12.5px] font-medium text-ink-2">Cod</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z-]/g, "").slice(0, 11))}
              inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456"
              className={`${SEL} tnum w-full tracking-[0.25em]`}
            />
            <p className="mt-1.5 text-[11.5px] text-ink-3">Nu ai telefonul la tine? Scrie aici unul dintre codurile de recuperare.</p>
          </div>
          {error && <p className="flex items-start gap-1.5 rounded-[var(--l-r-xs)] bg-neg-soft px-3 py-2 text-[12.5px] font-medium text-neg"><I name="alert" className="!size-4 shrink-0" />{error}</p>}
          <button type="submit" disabled={pending || code.length < 6} className={BTN}>
            {pending ? <Spinner /> : <I name="check" className="!size-4" />}Confirmă
          </button>
        </form>
        <button onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="mt-4 w-full text-center text-[12.5px] font-medium text-ink-3 transition hover:text-ink-2">
          Înapoi la autentificare
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border border-line bg-surface p-6 shadow-card">
      <p className="font-display text-[19px] font-medium text-ink">Autentificare</p>
      {resetat && (
        <p className="mt-4 flex items-start gap-1.5 rounded-[var(--l-r-xs)] bg-pos-soft/50 px-3 py-2 text-[12.5px] font-medium text-ink-2">
          <I name="check" className="!size-4 shrink-0 text-pos" />Parola a fost schimbată. Intră cu ea acum.
        </p>
      )}
      <form className="mt-5 flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div>
          <label className="mb-1 block text-[12.5px] font-medium text-ink-2">Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" autoFocus className={`${SEL} w-full`} />
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="block text-[12.5px] font-medium text-ink-2">Parolă</label>
            <Link href="/parola" className="text-[12px] font-medium text-ink-3 transition hover:text-accent">Am uitat parola</Link>
          </div>
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" className={`${SEL} w-full`} />
        </div>
        <CaptchaField code={capCode} onCode={setCapCode} onToken={setCapToken} reload={capReload} />
        {error && <p className="flex items-start gap-1.5 rounded-[var(--l-r-xs)] bg-neg-soft px-3 py-2 text-[12.5px] font-medium text-neg"><I name="alert" className="!size-4 shrink-0" />{error}</p>}
        <button type="submit" disabled={pending} className={BTN}>
          {pending ? <Spinner /> : <I name="check" className="!size-4" />}Intră în cont
        </button>
      </form>
    </div>
  );
}
